import { CheckIcon } from "lucide-react";
import { cn } from "@devhop/ui";
import type { EventFeaturesDTO, EventProgressDTO } from "../../domain/contracts";
import { eventHasFeature } from "../../domain/services/event-features";
import { formatPayoutStatusLabel } from "../lib/payout-status-ui";
import type { EventDetailSectionId } from "../lib/event-detail-ui";

type EventProgressCardProps = {
  progress: EventProgressDTO | null | undefined;
  eventFeatures: EventFeaturesDTO;
  onSectionClick?: (section: EventDetailSectionId) => void;
  className?: string;
};

function MilestoneRow({
  label,
  done,
  skipped,
  statusHint,
}: {
  label: string;
  done: boolean;
  skipped?: boolean;
  statusHint?: string | null;
}) {
  if (skipped) return null;
  return (
    <li className="flex items-center gap-2 text-sm">
      <span
        className={
          done
            ? "flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground"
            : "flex size-5 items-center justify-center rounded-full border border-border bg-muted/40 text-muted-foreground"
        }
      >
        {done ? <CheckIcon className="size-3" aria-hidden /> : null}
      </span>
      <span className={done ? "text-foreground" : "text-muted-foreground"}>
        {label}
      </span>
      {statusHint ? (
        <span className="ml-auto text-muted-foreground text-xs">{statusHint}</span>
      ) : null}
    </li>
  );
}

export function EventProgressCard({
  progress,
  eventFeatures,
  onSectionClick,
  className,
}: EventProgressCardProps) {
  const hasPerformers = eventHasFeature(eventFeatures, "performers");
  const hasStaff = eventHasFeature(eventFeatures, "staff");
  const hasDocuments = eventHasFeature(eventFeatures, "documents");
  const staffPrimary = hasStaff && !hasPerformers;
  const hasRehearsals = eventHasFeature(eventFeatures, "rehearsals");
  const hasProductionCosts = eventHasFeature(eventFeatures, "production_costs");

  const pct = progress?.operationalPercent ?? null;
  const m = progress?.milestones;
  const productionCostHint =
    hasProductionCosts && progress?.productionCostPercent != null
      ? `${progress.productionCostPercent}%`
      : null;
  const payoutStatusHint =
    (hasPerformers || staffPrimary) && progress
      ? (() => {
          const statusText = formatPayoutStatusLabel(progress.payoutStatus);
          if (
            progress.apPercent != null &&
            progress.payoutStatus !== "paid"
          ) {
            return `${statusText} · AP ${progress.apPercent}%`;
          }
          return statusText;
        })()
      : null;

  return (
    <div className={cn("mt-4 rounded-xl border bg-card p-5", className)}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">
            ຄວາມຄືບໜ້າປະຕິບັດການ
          </p>
          <p className="mt-1 text-muted-foreground text-xs">
            ລວມຮັບເງິນລູກຄ້າ — ຍອດ AR/AP ລະອຽດເບິ່ງແຍກ
          </p>
          {pct == null ? (
            <p className="mt-3 font-bold text-2xl text-muted-foreground">—</p>
          ) : (
            <>
              <p className="mt-3 font-bold text-3xl tabular-nums">{pct}%</p>
              <div className="mt-2 h-2 max-w-xs overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </>
          )}
        </div>

        <ul className="grid min-w-48 gap-2 sm:grid-cols-2">
          {hasPerformers ? (
            <MilestoneRow label="ຈັດທີມ" done={!!m?.team} />
          ) : null}
          {hasDocuments ? (
            <MilestoneRow
              label={staffPrimary ? "ໃບສະເໜີລາຄາ" : "ລາຄາ"}
              done={!!m?.pricing}
            />
          ) : null}
          {hasRehearsals ? (
            <MilestoneRow
              label="ວັນຊ້ອມ"
              done={!!m?.rehearsal}
              skipped={m?.rehearsal == null}
            />
          ) : null}
          {(hasPerformers || staffPrimary) ? (
            <MilestoneRow
              label="ເບີກຈ່າຍ"
              done={!!m?.payout}
              statusHint={payoutStatusHint}
            />
          ) : null}
          {staffPrimary ? (
            <MilestoneRow label="ໃບເກັບເງິນ" done={!!m?.invoiceIssued} />
          ) : null}
          {hasDocuments ? (
            <MilestoneRow label="ຮັບເງິນຄົບ" done={!!m?.paymentCollected} />
          ) : null}
          {hasProductionCosts ? (
            <MilestoneRow
              label="Prop / ວັດສະດຸ"
              done={!!m?.productionCosts}
              statusHint={productionCostHint}
            />
          ) : null}
        </ul>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 border-t pt-4 text-sm">
        <button
          type="button"
          className="rounded-md border px-3 py-1.5 text-left hover:bg-muted/40"
          onClick={() => onSectionClick?.("pricing")}
        >
          <span className="text-muted-foreground text-xs">AR · ເກັບເງິນ</span>
          <p className="font-semibold tabular-nums">
            {progress?.arPercent == null ? "—" : `${progress.arPercent}%`}
          </p>
        </button>
        {hasPerformers || staffPrimary ? (
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 text-left hover:bg-muted/40"
            onClick={() => onSectionClick?.("payout")}
          >
            <span className="text-muted-foreground text-xs">
              AP · {staffPrimary ? "ຈ່າຍ Staff" : "ຈ່າຍນັກສະແດງ"}
            </span>
            <p className="font-semibold tabular-nums">
              {progress?.apPercent == null ? "—" : `${progress.apPercent}%`}
            </p>
            {progress?.payoutStatus ? (
              <p className="text-muted-foreground text-xs">
                {formatPayoutStatusLabel(progress.payoutStatus)}
              </p>
            ) : null}
          </button>
        ) : null}
        {hasProductionCosts ? (
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 text-left hover:bg-muted/40"
            onClick={() => onSectionClick?.("production_costs")}
          >
            <span className="text-muted-foreground text-xs">Prop / ວັດສະດຸ</span>
            <p className="font-semibold tabular-nums">
              {progress?.productionCostPercent == null
                ? "—"
                : `${progress.productionCostPercent}%`}
            </p>
          </button>
        ) : null}
      </div>
    </div>
  );
}
